'use client'

import { useEffect, useRef, useState } from 'react'
import Tabordion, { TabordionItemType } from '@dustin-web/microsite-components/src/tabordion'
import { useTranslations } from '@dustin-web/microsite-components/src/hooks'
import ReviewStars from '@dustin-web/product-components/src/review-stars'
import Spinner from '@dustin-web/microsite-components/src/spinner/spinner'
import UserReviews from './product-reviews-user-review'
import ExpertReviews from './product-reviews-pro-reviews'
import { ProductPageType } from '..'
import ProductReviewsFooter from './product-reviews-footer'
import ReviewsPolicyLink from './reviews-policy-link'
import WriteReview from './product-write-review'
import './product-reviews.scss'
import { useProductReviewsNextPage } from '@productsite-beta/src/hooks/use-product-reviews-next-page'
import { GetProductReviewsSummaryQuery } from '../../../graphql/generated/graphql-types'

export type Props = {
  productErpIdentifier: ProductPageType['productErpIdentifier']
  displayName: ProductPageType['displayName']
  isVisible: boolean
  overviewData: GetProductReviewsSummaryQuery
}

const REVIEW_TAB__MAP: Record<string, number> = {
  CUSTOMER: 0,
  EXPERT: 1,
  WRITE: 2,
}

const nextPageQueryHistory: string[] = []

const ProductReviews = ({ overviewData, productErpIdentifier, displayName, isVisible }: Props) => {
  const { translate } = useTranslations()
  const [fetchPage, setFetchPage] = useState<boolean>(false)
  const [nextPageQuery, setNextPageQuery] = useState<string | undefined>()
  const { nextPageData, nextPageError, nextPageLoading } = useProductReviewsNextPage(
    //TODO: refactor this to not excecute on client
    productErpIdentifier,
    isVisible,
    fetchPage,
    setFetchPage,
    nextPageQuery
  )
  const [proPageCount, setProPageCount] = useState<number>(0)
  const [customerPageCount, setCustomerPageCount] = useState<number>(0)

  const [reviewActiveTabs, setReviewActiveTabs] = useState<number[]>(
    overviewData.productReviewSummary.reviewWriteAccess === 'ALLOWED'
      ? [REVIEW_TAB__MAP.WRITE]
      : overviewData.productReviewSummary.customerReviewCount
        ? [REVIEW_TAB__MAP.CUSTOMER]
        : [REVIEW_TAB__MAP.EXPERT]
  )

  const scrollToRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollToRef.current?.scrollIntoView()
  }, [proPageCount, customerPageCount])

  if (nextPageError) {
    return (
      <div className="container">
        <div className="u-pt-32 u-pb-24">
          <h3 className="u-mb-0">Something went wrong</h3>
        </div>
      </div>
    )
  }

  if (nextPageLoading) {
    return (
      <div className="u-relative u-bg-gray-02" style={{ height: '500px' }}>
        <Spinner local={true} size={'md'} />
      </div>
    )
  }

  const reviewObj = {
    pro: {
      reviewsData: overviewData.productReviewSummary.proReviews,
      reviewCount: overviewData.productReviewSummary.proReviewCount,
      nextPageQuery: overviewData.productReviewSummary.proReviews?.nextPageQuery,
      nextPageData: nextPageData?.productReviewPages,
      nextQueryHistory: [],
    },
    customer: {
      reviewsData: overviewData.productReviewSummary.customerReviews,
      reviewCount: overviewData.productReviewSummary.customerReviewCount,
      nextPageQuery: overviewData.productReviewSummary.customerReviews?.nextPageQuery,
      nextPageData: nextPageData?.productReviewPages,
      nextQueryHistory: [],
      avgScore: overviewData.productReviewSummary.averageUserScore,
    },
  }

  if (proPageCount !== 0 && nextPageData) {
    reviewObj.pro.nextPageData = nextPageData.productReviewPages
  } else if (customerPageCount !== 0 && nextPageData) {
    reviewObj.customer.nextPageData = nextPageData.productReviewPages
  }

  const onFetchPage = (query: string | null | undefined) => {
    if (query) {
      if (!nextPageQueryHistory.includes(query)) {
        nextPageQueryHistory.push(query)
      }

      setNextPageQuery(query)
      setFetchPage(true)
    }
  }

  const tabordionItems: TabordionItemType[] = [
    {
      title: `${translate('Product_Review_UserReviews')} (${reviewObj.customer.reviewCount})`,
      /**Temp: User reviews disabled */
      content: reviewObj.customer.reviewsData ? (
        <>
          <div className="review-card__reviewPolicies">
            <ReviewsPolicyLink />
          </div>
          <UserReviews userReviews={reviewObj.customer.reviewsData?.reviews} />
          <ProductReviewsFooter
            reviewsCount={reviewObj.customer.reviewCount}
            pageCount={customerPageCount}
            onFetchPrevPage={() => {
              customerPageCount !== 0
                ? setCustomerPageCount(customerPageCount - 1)
                : setCustomerPageCount(0)
              onFetchPage(
                customerPageCount !== 0
                  ? nextPageQueryHistory[customerPageCount - 2]
                  : nextPageQueryHistory[customerPageCount]
              )
            }}
            onFetchNextPage={() => {
              Math.floor(reviewObj.customer.reviewCount / 8) !== customerPageCount
                ? setCustomerPageCount(customerPageCount + 1)
                : setCustomerPageCount(Math.floor(reviewObj.customer.reviewCount / 8))
              onFetchPage(reviewObj.customer.nextPageQuery)
            }}
          />
        </>
      ) : null,
      noContentText: (
        <>
          <div className="review-card__reviewPoliciesNoContent">
            <ReviewsPolicyLink />
            {translate('NoContent_UserReviews')}
          </div>

          <ProductReviewsFooter
            reviewsCount={reviewObj.customer.reviewCount}></ProductReviewsFooter>
        </>
      ),
    },
    {
      title: `${translate('Product_Review_ProReviews')} (${reviewObj.pro.reviewCount})`,
      content: reviewObj.pro.reviewsData?.reviews ? (
        <>
          <ExpertReviews proReviews={reviewObj.pro.reviewsData?.reviews} />
          <ProductReviewsFooter
            reviewsCount={reviewObj.pro.reviewCount}
            pageCount={proPageCount}
            onFetchPrevPage={() => {
              proPageCount !== 0 ? setProPageCount(proPageCount - 1) : setProPageCount(0)
              onFetchPage(
                proPageCount !== 0
                  ? nextPageQueryHistory[proPageCount - 2]
                  : nextPageQueryHistory[proPageCount]
              )
            }}
            onFetchNextPage={() => {
              Math.floor(reviewObj.pro.reviewCount / 8) !== proPageCount
                ? setProPageCount(proPageCount + 1)
                : setProPageCount(Math.floor(reviewObj.pro.reviewCount / 8))
              onFetchPage(reviewObj.pro.nextPageQuery)
            }}
          />
        </>
      ) : null,
      noContentText: (
        <>
          <div className="review-card__reviewPoliciesNoContent">
            {translate('NoContent_ProReviews')}
          </div>
          <ProductReviewsFooter reviewsCount={reviewObj.pro.reviewCount} />
        </>
      ),
    },
    {
      title: translate('Product_Review_WriteNewReview'),
      content: (
        <WriteReview
          productErpIdentifier={productErpIdentifier}
          reviewWriteAccess={overviewData?.productReviewSummary?.reviewWriteAccess}
        />
      ),
      noContentText: (
        <div className="row">
          <div className="col-12 col-lg-8">{translate('WriteReviewsDisclaimer')}</div>
        </div>
      ),
    },
  ]

  return (
    <div className="c-product-reviews u-bg-gray-02" ref={scrollToRef}>
      <div className="container">
        <h3 className="u-mb-8 d-none d-sm-block">
          {translate('ReviewsOf')} {displayName}
        </h3>
        <div className="u-mb-16 u-mb-md-24">
          <div className="d-flex">
            <p className="u-fs-32 u-mr-12">{reviewObj.customer.avgScore.toFixed(1)}</p>
            <div className="u-mr-8 u-mb-8 d-flex align-items-center">
              <ReviewStars reviewScore={reviewObj.customer.avgScore} />
            </div>
          </div>
          <span className="u-color-gray-08">
            {`( ${reviewObj.customer.reviewCount} ${translate('Product_Review_UserReviews')})`}
          </span>
        </div>
        <div className="row">
          <div className="col">
            <Tabordion
              items={tabordionItems}
              extState
              extActiveTabs={reviewActiveTabs}
              setExtActiveTabs={x => setReviewActiveTabs(x)}
              noContentPadding
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductReviews
